"use client";

import * as React from "react";
import { useChromeStrings } from "./chrome-strings";
import { Minus, Plus } from "lucide-react";
import { PopoverContent } from "@/components/ui/popover";
import {
  useHeaderPopoverLayout,
  type ArticlePopoverPlacement,
} from "./popover-placement";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

export const FONT_STORAGE_KEY = "hkej-font-step";
const STORAGE_KEY = FONT_STORAGE_KEY;
/** The step LABELS are localised at render — this vertical set includes an
 *  English edition (EJ Insight), and the popover read 小 / 標準 / 大 / 特大 on
 *  it. QA: "change the text to english for ejinsight". The keys are stable; the
 *  words come from `messages/*.json`. */
const STEPS = [
  { scale: 0.88, key: "small" },
  { scale: 1, key: "standard" },
  { scale: 1.14, key: "large" },
  { scale: 1.3, key: "xlarge" },
] as const;

const BASE_SAMPLE_PX = 15;

// Apply the saved font scale to the document root before the popover mounts,
// so the saved size persists across articles. Place this near the top of any
// article page that reads --article-font-scale.
export function FontScaleBootstrap() {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const idx = saved ? parseInt(saved, 10) : 1;
      if (!isNaN(idx) && idx >= 0 && idx < STEPS.length) {
        document.documentElement.style.setProperty(
          "--article-font-scale",
          String(STEPS[idx].scale),
        );
      }
    } catch {}
  }, []);
  return null;
}

export function FontSizePopoverContent({
  placement = "rail",
}: {
  placement?: ArticlePopoverPlacement;
} = {}) {
  const layout = useHeaderPopoverLayout(placement, {
    desktopWidth: 320,
    headerAlign: "center",
  });

  const t = useChromeStrings("font");
  const [idx, setIdx] = React.useState(1);

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const n = parseInt(saved, 10);
        if (!isNaN(n) && n >= 0 && n < STEPS.length) {
          setIdx(n);
          apply(STEPS[n].scale);
        }
      }
    } catch {}
  }, []);

  const apply = (scale: number) => {
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(
        "--article-font-scale",
        String(scale),
      );
    }
  };

  const setStep = (n: number) => {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, n));
    setIdx(clamped);
    apply(STEPS[clamped].scale);
    try {
      localStorage.setItem(STORAGE_KEY, String(clamped));
    } catch {}
  };

  const atMin = idx === 0;
  const atMax = idx === STEPS.length - 1;

  return (
    <PopoverContent
      side={layout.side}
      align={layout.align}
      sideOffset={12}
      collisionPadding={layout.collisionPadding}
      className="bg-white p-0"
      style={{
        zIndex: 60,
        width: layout.width,
        borderRadius: "8px",
        border: `1px solid rgba(0,0,0,0.15)`,
        fontFamily: CORE_FONT.sans,
      }}
    >
      <div className="flex flex-col">
        {/* Header: label + current step name */}
        <div
          className="flex items-baseline justify-between px-[18px] pt-[14px] pb-[10px]"
          style={{ borderBottom: `1px solid ${CORE_COLORS.dividerSoft}` }}
        >
          <span
            style={{
              fontFamily: CORE_FONT.condensed,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: CORE_COLORS.textPrimary,
              lineHeight: "16px",
            }}
          >
            {t("heading")}
          </span>
          <span
            role="status"
            aria-live="polite"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "12px",
              fontWeight: 700,
              color: CORE_COLORS.brandGreenDeep,
              lineHeight: "16px",
            }}
          >
            {t(STEPS[idx].key)}
          </span>
        </div>

        {/* Live preview — real sample text at current scale */}
        <div
          className="px-[18px] py-[14px]"
          style={{ borderBottom: `1px solid ${CORE_COLORS.dividerSoft}` }}
        >
          <p
            className="transition-[font-size] duration-150 ease-out motion-reduce:transition-none"
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: `${BASE_SAMPLE_PX * STEPS[idx].scale}px`,
              fontWeight: 400,
              lineHeight: 1.6,
              color: CORE_COLORS.textPrimary,
            }}
          >
            {t("sample")}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-[10px] px-[18px] py-[14px]">
          <button
            type="button"
            aria-label={t("smaller")}
            aria-disabled={atMin}
            onClick={() => setStep(idx - 1)}
            disabled={atMin}
            className="inline-flex items-center justify-center size-[44px] rounded-full transition-[background-color,transform] duration-150 ease-out motion-reduce:transition-none hover:opacity-90 active:scale-[0.94] disabled:cursor-not-allowed disabled:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--green)]"
            style={
              {
                // Grey when at the minimum, not a dimmed green. `backgroundColor`
                // is set inline here, so a `disabled:bg-*` utility could never
                // win — the state has to be resolved in the style object.
                backgroundColor: atMin
                  ? CORE_COLORS.disabledSurface
                  : CORE_COLORS.brandGreenDeep,
                ["--green" as string]: CORE_COLORS.brandGreen,
              } as React.CSSProperties
            }
          >
            <Minus className="size-[18px]" color="white" strokeWidth={2.2} />
          </button>

          {/* Progress pips */}
          <div
            className="flex-1 flex items-center justify-center gap-[8px]"
            role="progressbar"
            aria-valuenow={idx + 1}
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
          >
            {STEPS.map((_, i) => (
              <span
                key={i}
                className="block rounded-full transition-all duration-200 ease-out motion-reduce:transition-none"
                style={{
                  width: i === idx ? 20 : 8,
                  height: 8,
                  backgroundColor:
                    i === idx ? CORE_COLORS.brandGreen : "rgba(0,0,0,0.15)",
                }}
              />
            ))}
          </div>

          <button
            type="button"
            aria-label={t("larger")}
            aria-disabled={atMax}
            onClick={() => setStep(idx + 1)}
            disabled={atMax}
            className="inline-flex items-center justify-center size-[44px] rounded-full transition-[background-color,transform] duration-150 ease-out motion-reduce:transition-none hover:opacity-90 active:scale-[0.94] disabled:cursor-not-allowed disabled:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--green)]"
            style={
              {
                // Grey at the maximum — see the matching note on the − button.
                backgroundColor: atMax
                  ? CORE_COLORS.disabledSurface
                  : CORE_COLORS.brandGreen,
                ["--green" as string]: CORE_COLORS.brandGreen,
              } as React.CSSProperties
            }
          >
            <Plus className="size-[18px]" color="white" strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </PopoverContent>
  );
}
